'use client';

import React, { Component, ReactNode } from 'react';
import { AppUpdateScreen } from '@/components/app-update-screen';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { isDeploySkewCrash } from '@/lib/deploy-skew';
import { reportPageCrash } from '@/lib/observability/user-blocking-failure-reporter';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /**
   * The fallback replaces a page or panel the user was working in, so the
   * crash alerts #alerting (#6012). Without a fallback it always does.
   */
  reportCrash?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  /** The tab is out of date with a new deploy; a reload fixes it. */
  deploySkew?: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, deploySkew: isDeploySkewCrash(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // A caller fallback is a designed degradation unless it opts in.
    if (!this.props.fallback || this.props.reportCrash)
      reportPageCrash(error, { deploySkew: this.state.deploySkew });
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.deploySkew)
        return <AppUpdateScreen compact error={this.state.error} />;
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle className="text-xl font-semibold text-red-600">
                Oops! Something went wrong
              </CardTitle>
              <CardDescription>
                This component encountered an unexpected error.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-medium text-red-700">
                    Error: {this.state.error.message}
                  </p>
                </div>
              )}

              <Button
                onClick={() => this.setState({ hasError: false })}
                className="w-full"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorFallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={errorFallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
